export const computeDisplayLabels = (questions, startOffset = 0) => {
  const labelMap = {};
  let baseNumber = startOffset;
  const subCounters = {};
  for (const q of questions) {
    if (q.conditional && q.conditional.dependsOn) {
      const parentId = q.conditional.dependsOn;
      if (!subCounters[parentId]) subCounters[parentId] = 0;
      subCounters[parentId] += 1;
      const parentLabel = labelMap[parentId] || String(baseNumber);
      labelMap[q.id] = `${parentLabel}.${subCounters[parentId]}`;
    } else {
      baseNumber += 1;
      labelMap[q.id] = String(baseNumber);
    }
  }
  return labelMap;
};

const DEFAULT_PAGE_TITLE = 'Seite 1';
const PAGE_TYPE_BASIS = 'basis';
const PAGE_TYPE_FACH = 'fach';

const normalizePageType = (pageType, fallbackType = PAGE_TYPE_FACH) => {
  const normalized = String(pageType || '').trim().toLowerCase();
  if (normalized === PAGE_TYPE_BASIS || normalized === PAGE_TYPE_FACH) {
    return normalized;
  }
  return fallbackType;
};

export const generateQuestionPageId = () => {
  return `page_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
};

export const createQuestionPage = ({ id, title, description, questions = [], order, pageType } = {}) => {
  return {
    id: id || generateQuestionPageId(),
    title: (title || '').trim() || DEFAULT_PAGE_TITLE,
    description: (description || '').trim(),
    order,
    pageType: normalizePageType(pageType, PAGE_TYPE_FACH),
    questions: Array.isArray(questions) ? questions : []
  };
};

export const flattenQuestionPages = (questionPages = []) => {
  if (!Array.isArray(questionPages)) return [];

  return questionPages.flatMap((page) => {
    const pageId = page?.id;
    const pageTitle = (page?.title || '').trim();
    const pageDescription = (page?.description || '').trim();
    const pageType = normalizePageType(page?.pageType, PAGE_TYPE_FACH);
    const pageQuestions = Array.isArray(page?.questions) ? page.questions : [];

    return pageQuestions.map((question) => ({
      ...question,
      pageId: question?.pageId || pageId,
      pageTitle: question?.pageTitle || pageTitle,
      pageDescription: question?.pageDescription || pageDescription,
      pageType: question?.pageType || pageType
    }));
  });
};

const sanitizeQuestionPages = (questionPages = []) => {
  const orderedPages = Array.isArray(questionPages)
    ? [...questionPages].sort((a, b) => {
        const aOrder = Number.isFinite(Number(a?.order)) ? Number(a.order) : Number.MAX_SAFE_INTEGER;
        const bOrder = Number.isFinite(Number(b?.order)) ? Number(b.order) : Number.MAX_SAFE_INTEGER;
        return aOrder - bOrder;
      })
    : [];

  return orderedPages.map((page, pageIndex) => {
    const pageId = page?.id || generateQuestionPageId();
    const pageQuestions = Array.isArray(page?.questions) ? page.questions : [];
    const fallbackType = pageIndex === 0 ? PAGE_TYPE_BASIS : PAGE_TYPE_FACH;

    return {
      id: pageId,
      title: typeof page?.title === 'string' ? page.title.trim() : `Seite ${pageIndex + 1}`,
      description: (page?.description || '').trim(),
      order: pageIndex + 1,
      pageType: normalizePageType(page?.pageType, fallbackType),
      questions: pageQuestions.map((question) => ({
        ...question,
        pageId
      }))
    };
  });
};

const sortPagesByOrder = (pages = []) => {
  return [...pages].sort((a, b) => {
    const aOrder = Number.isFinite(Number(a?.order)) ? Number(a.order) : Number.MAX_SAFE_INTEGER;
    const bOrder = Number.isFinite(Number(b?.order)) ? Number(b.order) : Number.MAX_SAFE_INTEGER;
    return aOrder - bOrder;
  });
};

const buildQuestionPagesFromFlatQuestions = (questions = [], pageMeta = []) => {
  const normalizedQuestions = Array.isArray(questions) ? questions : [];
  const metaPages = sanitizeQuestionPages(pageMeta);
  const pageMap = new Map();

  if (metaPages.length > 0) {
    metaPages.forEach((page) => {
      pageMap.set(page.id, {
        id: page.id,
        title: page.title,
        description: (page.description || '').trim(),
        order: page.order,
        pageType: normalizePageType(page.pageType, PAGE_TYPE_FACH),
        questions: []
      });
    });
  }

  if (pageMap.size === 0) {
    normalizedQuestions.forEach((question) => {
      const pageId = question?.pageId;
      if (!pageId || pageMap.has(pageId)) return;

      pageMap.set(pageId, {
        id: pageId,
        title: typeof question?.pageTitle === 'string' ? question.pageTitle.trim() : `Seite ${pageMap.size + 1}`,
        description: (question?.pageDescription || '').trim(),
        order: Number.isFinite(Number(question?.pageOrder)) ? Number(question.pageOrder) : pageMap.size + 1,
        pageType: normalizePageType(question?.pageType, pageMap.size === 0 ? PAGE_TYPE_BASIS : PAGE_TYPE_FACH),
        questions: []
      });
    });
  }

  if (pageMap.size === 0) {
    const fallbackPage = createQuestionPage({ title: DEFAULT_PAGE_TITLE, order: 1, questions: [] });
    pageMap.set(fallbackPage.id, fallbackPage);
  }

  normalizedQuestions.forEach((question) => {
    if (question?.pageId && !pageMap.has(question.pageId)) {
      pageMap.set(question.pageId, {
        id: question.pageId,
        title: typeof question?.pageTitle === 'string' ? question.pageTitle.trim() : `Seite ${pageMap.size + 1}`,
        description: (question?.pageDescription || '').trim(),
        order: Number.isFinite(Number(question?.pageOrder)) ? Number(question.pageOrder) : pageMap.size + 1,
        pageType: normalizePageType(question?.pageType, pageMap.size === 0 ? PAGE_TYPE_BASIS : PAGE_TYPE_FACH),
        questions: []
      });
    }
  });

  const hydratedPages = sortPagesByOrder(Array.from(pageMap.values()));
  const hydratedFirstPageId = hydratedPages[0]?.id;

  normalizedQuestions.forEach((question) => {
    const hasKnownPageId = question?.pageId && pageMap.has(question.pageId);
    const questionPageId = hasKnownPageId ? question.pageId : hydratedFirstPageId;
    const targetPage = pageMap.get(questionPageId);

    if (!targetPage) return;

    targetPage.questions.push({
      ...question,
      pageId: questionPageId
    });
  });

  return sanitizeQuestionPages(Array.from(pageMap.values()));
};

export const normalizeQuestionnaireData = (data) => {
  const source = data && typeof data === 'object' ? data : {};
  const sourceQuestions = Array.isArray(source.questions) ? source.questions : [];
  const sourcePageMeta = source.questionPages || source.intro_page?.questionPages || [];

  let questionPages = sanitizeQuestionPages(sourcePageMeta);
  const hasNestedPageQuestions = questionPages.some((page) => (page?.questions || []).length > 0);

  if (!hasNestedPageQuestions && sourceQuestions.length > 0) {
    questionPages = buildQuestionPagesFromFlatQuestions(sourceQuestions, sourcePageMeta);
  }

  if (questionPages.length === 0) {
    const fallbackQuestions = sourceQuestions;
    questionPages = [
      createQuestionPage({
        title: DEFAULT_PAGE_TITLE,
        pageType: PAGE_TYPE_BASIS,
        order: 1,
        questions: fallbackQuestions
      })
    ].map((page, index) => ({
      ...page,
      order: index + 1,
      questions: (page.questions || []).map((question) => ({
        ...question,
        pageId: page.id
      }))
    }));
  }

  const questions = flattenQuestionPages(questionPages);

  return {
    ...source,
    questionPages,
    questions
  };
};

export const getQuestionsFromQuestionnaireData = (data) => {
  const normalized = normalizeQuestionnaireData(data);
  return normalized.questions || [];
};

export const getQuestionPagesFromQuestionnaireData = (data) => {
  const normalized = normalizeQuestionnaireData(data);
  return normalized.questionPages || [];
};
